import React from 'react';
import useSkills from '@utils/hooks/application/use-skills';
import { SkillsSliderField } from '@components/Molecules/SkillsSliderField';
import { useTranslation } from 'react-i18next';
import { useStoreState } from 'easy-peasy';
import { jobStoreSelector } from '@model/Job/Job.selectors';

const SkillTypes = {
  MUST_HAVE: 'must-have',
  NICE_TO_HAVE: 'nice-to-have',
};

export const Skills = ({ candidateSkills, isScoutReportAvailable }) => {
  const { t } = useTranslation();
  const job = useStoreState(jobStoreSelector);
  const { requirements } = job || {};
  const { must_have, nice_to_have } = useSkills(candidateSkills, requirements);

  return candidateSkills?.length > 0 ? (
    <div className="rev-o-container">
      {must_have.length > 0 ? (
        <SkillGroup
          type={SkillTypes.MUST_HAVE}
          collection={must_have}
          isScoutReportAvailable={isScoutReportAvailable}
        />
      ) : null}

      {nice_to_have.length > 0 ? (
        <SkillGroup
          type={SkillTypes.NICE_TO_HAVE}
          collection={nice_to_have}
          isScoutReportAvailable={isScoutReportAvailable}
        />
      ) : null}
    </div>
  ) : null;
};

const SkillGroup = ({ type, collection, isScoutReportAvailable }) => {
  const { t } = useTranslation();

  const title = getTitle(type);

  return (
    <>
      <div className="rev-o-grid">
        <div className="rev-o-col-12 rev-u-px-16 rev-u-mt-40">
          <div className="el-separator--solid rev-u-pb-40 rev-u-border-color-grey-60" />
          <p className="rev-o-typo-heading-5 rev-u-color-primary-100">
            {t(title)}
          </p>
        </div>
      </div>
      <div className="rev-o-grid">
        {collection.map((skill, idx) => (
          <SkillItem
            {...skill}
            key={`${skill.jobRequirement.id}_${idx}`}
            isScoutReportAvailable={isScoutReportAvailable}
          />
        ))}
      </div>
    </>
  );
};

const SkillItem = ({ evaluation, jobRequirement, isScoutReportAvailable }) => {
  const { t } = useTranslation();

  const { description, requiresScoutValidation } = jobRequirement;

  const { variant, messageI18nKey } = getHint(
    isScoutReportAvailable,
    requiresScoutValidation,
  );

  return (
    <div className="rev-u-px-16 rev-o-col-6 rev-o-col--medium-2 rev-u-pt-32">
      <SkillsSliderField
        label={description}
        value={evaluation}
        variant={variant === 'warning' ? 'warning' : 'primary'}
        hint={{
          variant,
          message: t(messageI18nKey),
        }}
      />
    </div>
  );
};

// --- Helpers
const getHint = (isScoutReportAvailable, isScoutValidationRequired) => {
  if (isScoutReportAvailable && isScoutValidationRequired) {
    return {
      variant: 'success',
      messageI18nKey: 'validation.scout_approved',
    };
  }

  if (isScoutValidationRequired) {
    return {
      variant: 'warning',
      messageI18nKey: 'validation.scout_required',
    };
  }

  return {
    variant: 'primary',
    messageI18nKey: '',
  };
};

const getTitle = type => {
  switch (type) {
    case SkillTypes.MUST_HAVE:
      return 'showcase.tableRows.must_have';
    case SkillTypes.NICE_TO_HAVE:
      return 'showcase.tableRows.nice_to_have';
    default:
      return '';
  }
};
